import './JobLogAnnotations.scss';

import { IonAlert, IonList } from '@ionic/react';
import { FC, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { GenericMessages } from '../../intl/GenericMessages';
import { JobMessages } from '../../intl/JobMessages';
import { AppModal } from '../reusable-components/app-modal/AppModal';
import { AddAnnotation } from './AddAnnotation';
import { AnnotationRow } from './AnnotationRow';
import { AnnotationViewModel, useAnnotationsContext } from './AnnotationsContext';
import { Input } from '../reusable-components/Input';

export interface JobLogAnnotationsProps {
  /** The currently selected timestamp on the graph. */
  timestamp: number | undefined;
  /** The timezone associated with the job log. */
  timezone: string;
  /** whether tho show timestamps */
  showTimestamps: boolean;
  /** whether to show annotation actions */
  showAnnotationActions: boolean;
  /** whether to show the user who made the annotation */
  showAnnotationUser: boolean;
  /** The fallback user to display if the annotation user is not available. */
  fallbackUser: string | undefined;
}

/**
 * A component for displaying annotations associated with a job log.
 */
export const JobLogAnnotations: FC<JobLogAnnotationsProps> = ({
  timestamp,
  showTimestamps,
  showAnnotationActions,
  showAnnotationUser,
  timezone,
  fallbackUser,
}) => {
  const intl = useIntl();
  const [updating, setUpdating] = useState<AnnotationViewModel>();
  const [editedComment, setEditedComment] = useState<string>('');
  const [deleting, setDeleting] = useState<AnnotationViewModel>();
  const [deletedAfterLastPageRefresh, setDeletedAfterLastPageRefresh] = useState<string[]>([]);
  const { annotations, create, update, remove, restore } = useAnnotationsContext();

  useEffect(() => {
    setDeletedAfterLastPageRefresh([]);
  }, []);

  const onCreate = async (c: string, t: number | undefined) => {
    try {
      await create(c, t);
    } catch (e) {
      console.error('Failed to create annotation:', e); // todo: error banner?
    }
  };

  const onUpdate = (annotation: AnnotationViewModel) => {
    setUpdating(annotation);
    setEditedComment(annotation.comment);
  };

  const onDelete = (annotation: AnnotationViewModel) => {
    setDeleting(annotation);
  };

  const performUpdate = async (_id: string, comment: string) => {
    try {
      await update(_id, comment);
      setUpdating(undefined);
    } catch (e) {
      console.error('Failed to perform update:', e); // todo: error banner?
    }
  };

  const performDelete = async (_id: string) => {
    setDeletedAfterLastPageRefresh(recent => [...recent, _id]);

    try {
      await remove(_id);
      setDeleting(undefined);
    } catch (e) {
      console.warn('Failed to perform delete:', e); // todo: error banner?
    }
  };

  const performRestore = async (_id: string) => {
    try {
      await restore(_id);
      setDeletedAfterLastPageRefresh(recent => recent.filter(i => i !== _id));
    } catch (e) {
      console.warn('Failed to perform delete:', e); // todo: error banner?
    }
  };

  return (
    <>
      {showAnnotationActions && <AddAnnotation timestamp={timestamp} onSave={onCreate} timezone={timezone} />}
      <IonList className="job-log-annotations-list">
        {annotations.map(a => {
          const recent = deletedAfterLastPageRefresh.find(deleted => deleted === a.id);
          return (
            <AnnotationRow
              key={a.id}
              annotation={a}
              onUpdate={onUpdate}
              onDelete={onDelete}
              onRestore={e => performRestore(e.id)}
              deletedAfterLastPageRefresh={recent !== undefined}
              showTimestamps={showTimestamps}
              showAnnotationActions={showAnnotationActions}
              showAnnotationUser={showAnnotationUser}
              fallbackUser={fallbackUser || intl.formatMessage(GenericMessages.unknownPlaceholder)}
              timezone={timezone}
            />
          );
        })}
        {/* Update prompt */}
        <AppModal
          isOpen={!!updating}
          title={intl.formatMessage(JobMessages.jobInfoUpdateAnnotation)}
          onClose={() => setUpdating(undefined)}
          primaryButton={{
            label: intl.formatMessage(GenericMessages.saveButtonLabel),
            onClick: () => performUpdate(updating!.id, editedComment),
          }}
          secondaryButton={{
            label: intl.formatMessage(GenericMessages.cancelButtonLabel),
            onClick: () => setUpdating(undefined),
          }}
        >
          <Input value={editedComment} onChange={e => setEditedComment(e.detail.value)} id="job-log-annotations" />
        </AppModal>

        {/* Delete prompt */}
        <IonAlert
          isOpen={!!deleting}
          message={intl.formatMessage(JobMessages.jobInfoDeleteAnnotationConfirmation)}
          buttons={[
            {
              text: intl.formatMessage(GenericMessages.noButtonLabel),
              handler: () => setDeleting(undefined),
            },
            {
              text: intl.formatMessage(GenericMessages.yesButtonLabel),
              handler: () => performDelete(deleting!.id),
            },
          ]}
          onDidDismiss={() => setDeleting(undefined)}
        />
      </IonList>
    </>
  );
};
